<template>
  <section
    v-if="isValidProductType"
    class="specs"
    :class="[`specs--${productType}`, `specs--${theme}`, { 'specs--comparison': canCompare }]"
    v-data-test-id="'specs'"
    ref="specs"
  >
    <div v-if="canCompare">
      <h3 class="specs__headline">{{ $t('headline_mobile') }}</h3>
      <div class="specs__intro">
        {{ $t('intro') }}
      </div>
      <a :href="getFullUrl(`${FILTER_PAGE_PATH}/${productType}`)" class="specs__intro-link">
        {{ $t(`see_all_${productType}`) }}
      </a>
    </div>
    <h3 v-else class="specs__headline">{{ $t('headline') }}</h3>
    <div class="specs__results">
      <template v-if="hasSomeItem">
        <div v-for="(item, id, i) in visibleItems" class="specs__container" :key="`item-${id}`">
          <transition name="fade" mode="out-in">
            <div v-if="isLoading[id]" class="specs__loader-wrapper">
              <LoadingIndicator
                :type="LOADING_INDICATOR_TYPES.cloudCentered"
                class="specs__loader"
              />
            </div>
            <div v-else class="specs__spec-wrapper">
              <div class="specs__compare-selector-block" v-if="canCompare">
                <div class="specs__product-image-container">
                  <a class="specs__product-image-link" :href="item.productUrl">
                    <ImageContentful
                      v-if="item.defaultImage"
                      class="specs__product-image"
                      :isBackgroundImage="true"
                      :src="item.defaultImage.mediaUrl || ''"
                      :options="{
                        fixedWidth: 300,
                        fixedHeight: 300,
                        retinaSupportFactor: 0,
                      }"
                    />
                    <component v-else class="specs__silhouette" :is="currentTypeIcon" />
                  </a>
                </div>
                <CompareSelector
                  :id="i"
                  class="specs__compare-selector"
                  :productType="productType"
                  :theme="theme"
                  :initialValue="item.slug"
                  :defaultOptionText="$t('choose_model')"
                  :disabledOptionValue="items[1 - i].slug || ''"
                  @select="setSlugs"
                />
              </div>
              <div v-if="isEmpty(item)" class="specs__cta-text">
                {{ $t('choose_request') }}
              </div>
              <template v-else>
                <Spec
                  :class="{ 'spec--compared-product': i > 0 }"
                  :spec="item"
                  :productType="productType"
                  :isComparisonTool="canCompare"
                  :isSeparatorVisible="isMqLarge && hasSomeItem"
                  :isVerticalLayout="isVerticalLayout"
                  :selectedGender="selectedGender"
                  :theme="theme"
                  v-data-test-id="'spec'"
                />

                <div v-if="isStandalone" class="specs__product-cta-container">
                  <Button
                    class="specs__product-cta"
                    tag="a"
                    :height="isMqLarge ? BUTTON_STYLES.height.medium : BUTTON_STYLES.height.small"
                    :href="item.productUrl"
                  >
                    {{ $t('more_details') }}
                  </Button>
                </div>
              </template>
            </div>
          </transition>
        </div>
      </template>
      <div
        v-else-if="canCompare"
        class="specs__container specs__add-comparison specs__compared-product"
        v-data-test-id="`comparison-container`"
      >
        <transition name="fade" mode="out-in">
          <LoadingIndicator
            v-if="isLoading.initial"
            :type="LOADING_INDICATOR_TYPES.cloudCentered"
            class="specs__loader"
          />
          <div v-else class="specs__spec-wrapper">
            <div class="specs__compare-selector-block">
              <div class="specs__product-image-container">
                <component class="specs__silhouette" :is="currentTypeIcon" />
              </div>
              <CompareSelector
                :theme="theme"
                :productType="productType"
                :id="0"
                :defaultOptionText="$t('choose_model')"
                class="specs__compare-selector"
                @select="setSlugs"
              />
            </div>
            <div class="specs__cta-text">
              {{ $t('choose_request') }}
            </div>
          </div>
        </transition>
      </div>
    </div>
  </section>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import to from 'await-to-js'
import { FILTER_PAGE_PATH } from '@/config/constants'
import client from '@/api/client'
import productGroupForComparisonQuery from '@/api/gql/product/query/product-group-for-comparison.gql'
import { isEmpty } from '@/helpers/object'
import { loadForAtLeast } from '@/helpers/request'
import { registerStores } from '@/helpers/stores'
import { capitalizeFirstLetter } from '@/helpers/string'
import { GTM_CONTEXTS } from '@/services/external-communication/gtm/gtm-data-layer'
import productStore from '@/stores/product'
import pageStore from '@/stores/page'
import viewportStore from '@/stores/viewport'
import flash from '@/mixins/flash'
import { SPECS_THEMES, SPECS_PRODUCT_TYPES, SPECS_LOADING_WAIT_TIME } from './constants'
import { isValidSpecsProductType } from './helpers'
import ImageContentful from '@/components/_shared/ImageContentful.vue'
import IconShoesSilhouette from '@/components/_icons/IconShoesSilhouette.svg'
import IconApparelSilhouette from '@/components/_icons/IconApparelSilhouette.svg'
import IconAccessoriesSilhouette from '@/components/_icons/IconAccessoriesSilhouette.svg'
import Button, { BUTTON_STYLES } from '@/components/_shared/Button.vue'
import LoadingIndicator, {
  LOADING_INDICATOR_TYPES,
} from '@/components/_shared/LoadingIndicator.vue'
import Spec from '@/components/_shared/Specs/Spec/Spec.vue'
import CompareSelector from '@/components/_shared/Specs/CompareSelector/CompareSelector.vue'

export default {
  name: 'Specs',

  components: {
    CompareSelector,
    IconShoesSilhouette,
    IconApparelSilhouette,
    IconAccessoriesSilhouette,
    ImageContentful,
    LoadingIndicator,
    Spec,
    Button,
  },

  mixins: [flash],

  props: {
    isComparisonTool: {
      type: Boolean,
      default: true,
    },

    isIframeLayout: Boolean,

    isVerticalLayout: {
      type: Boolean,
      default: false,
    },

    productType: {
      type: String,
      default: SPECS_PRODUCT_TYPES.shoes,
    },

    slugs: {
      type: Array,
      default: () => [],
    },

    theme: {
      type: String,
      default: SPECS_THEMES.dark,
      validator(theme) {
        return Object.keys(SPECS_THEMES).includes(theme)
      },
    },

    isStandalone: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      FILTER_PAGE_PATH,
      isEmpty,
      isLoading: {
        initial: true,
        0: false,
        1: false,
      },
      SPECS_PRODUCT_TYPES,
      BUTTON_STYLES,
      localSlugs: [],
      items: { 0: {}, 1: {} },
      LOADING_INDICATOR_TYPES,
    }
  },

  computed: {
    ...mapState('product', ['selectedGender']),

    ...mapGetters({
      isMqLarge: 'viewport/isMqLarge',
      getFullUrl: 'page/getFullUrl',
    }),

    visibleItems() {
      return this.canCompare ? this.items : { 0: this.items[0] }
    },

    filteredSlugs() {
      return [...new Set(this.slugs)].slice(0, 2)
    },

    activeSlugs() {
      return Object.values(this.items).reduce(
        (slugs, item) => (item?.slug ? [...slugs, item.slug] : slugs),
        []
      )
    },

    hasSomeItem() {
      return Object.values(this.items).some(item => !isEmpty(item))
    },

    isValidProductType() {
      return isValidSpecsProductType(this.productType)
    },

    canCompare() {
      return this.isComparisonTool && this.isValidProductType
    },

    currentTypeIcon() {
      return `Icon${capitalizeFirstLetter(this.productType)}Silhouette`
    },
  },

  watch: {
    filteredSlugs: {
      immediate: true,
      deep: true,
      handler(filteredSlugs) {
        if (this.isValidProductType) this.setItemsFromSlugs(filteredSlugs)
      },
    },

    isLoading: {
      deep: true,
      immediate: true,
      handler(isLoading) {
        Object.values(isLoading).some(status => !!status)
          ? this.$emit('loading')
          : this.$emit('loaded')
      },
    },
  },

  beforeCreate() {
    registerStores({
      product: productStore,
      page: pageStore,
      viewport: viewportStore,
    })
  },

  methods: {
    setItemsFromSlugs(slugs = []) {
      if (slugs.length === 0) {
        this.isLoading.initial = false
        this.items = { 0: {}, 1: {} }
        return
      }
      if (slugs.length === 1) this.items[1] = {}

      slugs.forEach(async (slug, i) => {
        // already fetched
        if (this.activeSlugs.some(item => item === slug)) {
          this.isLoading.initial = false
          return
        }

        this.isLoading[i] = true
        this.items[i] = {}
        this.$_flash_clearMessages()

        const [error, result] = await this.fetchItem(slug)

        this.isLoading.initial = false
        this.isLoading[i] = false

        const productSpecType = result?.data?.productGroup?.productData?.productSpecType

        if (
          error ||
          !productSpecType ||
          this.productType !== this.getProductTypeFromSpec(productSpecType)
        ) {
          this.$_flash_addErrorMessage(
            error,
            this.$t('errors.invalid_product_for_comparison'),
            GTM_CONTEXTS.pdpCompareShoes
          )
          return
        }

        const {
          data: {
            productGroup: {
              name,
              productData: {
                productSpec,
                mainVariant: { defaultImage, spreeProduct },
              },
            },
          },
        } = result

        this.items[i] = {
          ...productSpec,
          defaultImage,
          productUrl: this.isIframeLayout
            ? `${spreeProduct.productUrl}?iframe=true`
            : spreeProduct.productUrl,
          slug,
          name,
        }

        this.updateURL()

        // track
        const items = [
          {
            id: spreeProduct.sku,
            price: spreeProduct.price,
            list: this.$CONTEXTS.shoeComparison,
            position: i,
          },
        ]
        this.$notifier.trackEvent({
          action: this.$notifier.ACTIONS.listView,
          productData: {
            items,
          },
        })

        this.$notifier.trackEvent({
          action: this.$notifier.ACTIONS.shoeComparison,
          details: { action: this.items[0]?.name, label: this.items[1]?.name },
        })
      })
    },

    async fetchItem(slug) {
      return await to(
        loadForAtLeast(
          client.query({ query: productGroupForComparisonQuery, variables: { slug } }),
          SPECS_LOADING_WAIT_TIME
        )
      )
    },

    setSlugs(option) {
      if (option.slug === '') return

      this.isLoading.initial = true

      const newSlugs = [...this.filteredSlugs]
      newSlugs[option.id] = option.slug

      this.$emit('update:slugs', newSlugs)
    },

    updateURL() {
      let specs

      if (this.isStandalone) {
        // update both slugs in standalone mode
        specs = this.activeSlugs.join('+')
      } else if (this.activeSlugs.length > 1) {
        // update only the last slug
        specs = this.activeSlugs[1]
      }

      if (specs) {
        to(
          this.$router.replace({
            query: { specs },
          })
        )
      }
    },

    getProductTypeFromSpec(specType) {
      let type = specType ? specType.replace(/(product|spec)/gi, '').toLowerCase() : ''
      if (type === 'shoe') type = SPECS_PRODUCT_TYPES.shoes
      return type
    },
  },
}
</script>

<style lang="scss" scoped>
@import './Specs';
</style>

<i18n src="@/locales/pdp_specs.json">
</i18n>
